import ReactDOM from 'react-dom';
import React, {Fragment} from 'react';
import { observer } from 'mobx-react';

import parseHtml from '../../utils/parseHtml';
import { noop } from '../../utils/index';

import {
  ThemedShortlistAlert,
  ThemedShortlistFormSubmit,
  ThemedShortlistFormWrapper,
  ThemedShortlistBuyButton,
  ThemedShortlistRemoveButton,
  ThemedShortlistSessionSelect,
} from '../../themes';


const ShortlistOrderForm = observer(({ action, returnUrl, shortlistStore }) => {
  const { buyingSessions, buyingSessionsCount } = shortlistStore;
  const hasNoItemsToBuy = buyingSessionsCount === 0;
  return (
    <form
      method='post'
      action={ action }
    >
      { returnUrl && <input type='hidden' name='third_party_url' value={ returnUrl } /> }
      { buyingSessions.map(session => {
        if (session.thirdPartyId) {
          return (
            <input
              key={ session.id }
              readOnly
              type='hidden'
              name='child_event_ids'
              value={ session.thirdPartyId }
            />
          );
        }
        return <Fragment key={ session.id } />;
      }) }
      <ThemedShortlistFormSubmit isDisabled={ hasNoItemsToBuy } />
    </form>
  );
});

const ShortlistOrderFormWrapper = observer(({ action, returnUrl, shortlistStore, parsedContent }) => {
  const { buyingSessionsCount, buyingItems } = shortlistStore;
  const orderForm = <ShortlistOrderForm action={ action } returnUrl={ returnUrl} shortlistStore={ shortlistStore } />;
  return (
    <ThemedShortlistFormWrapper cartCount={ buyingSessionsCount } cartItems={ buyingItems } form={ orderForm } parsedContent={ parsedContent } />
  );
});

const ShortlistEvent = observer(({ item, parsedContent }) => {
  if (item.isShortlisted) {
    return (
      parsedContent
    );
  }
  return <></>;
});

const ShortlistBuyButton = observer(({ item }) => {
  const hasSession = !!item.selectedSession;
  const onClick = hasSession ? item.toggleBuying : noop;
  return (
    <ThemedShortlistBuyButton
      onClick={ onClick }
      isDisabled={ !hasSession }
      isBuying={ item.isBuying }
      item={ item }
    />
  );
});

const ShortlistRemoveButton = observer(({ item }) => {
  return (
    <ThemedShortlistRemoveButton
      onClick={ item.toggleShortlist }
    />
  );
});

const ShortlistSessionSelect = observer(({ id, item }) => {
  const handleOnChange = event => {
    item.setSelectedSession(event.target.value);
  };
  const { sessions } = item;
  const hasMultipleSessions = sessions.length > 1;
  const getDefaultValue = () => {
    if (sessions.length >= 1) {
      const sessionId = sessions[0].id;
      item.setSelectedSession(sessionId);
      return sessionId;
    }
    return '';
  };

  return (
    <ThemedShortlistSessionSelect
      id={ id }
      sessions={ sessions }
      defaultValue={ getDefaultValue() }
      onChange={ handleOnChange }
      disabled={ !hasMultipleSessions }
    />
  );
});

const ShortlistAlert = observer(({ shortlistStore }) => {
  return (
    <ThemedShortlistAlert store={ shortlistStore } />
  );
});

const initEvents = store => {
  document.querySelectorAll('[data-shortlist-event]').forEach(el => {
    const eventId = el.getAttribute('data-event-id');
    const item = store.getItem(eventId);

    const hasDataAttribute = (attrib) => node => {
      return node.attribs && attrib in node.attribs;
    };

    const processingInstructions = [
      {
        shouldProcessNode: hasDataAttribute('data-shortlist-buy'),
        processNode: () => <ShortlistBuyButton item={ item } />,
      },
      {
        shouldProcessNode: hasDataAttribute('data-shortlist-order-toggle'),
        processNode: () => <ShortlistRemoveButton item={ item } store={ store } />,
      },
      {
        shouldProcessNode: hasDataAttribute('data-shortlist-session-select'),
        processNode: node => {
          const id = node.attribs.id;
          return <ShortlistSessionSelect id={ id } item={ item } />;
        },
      },
    ];

    const parsedContent = parseHtml(el.innerHTML, processingInstructions);
    ReactDOM.render(
      <ShortlistEvent item={ item } parsedContent={ parsedContent } />,
      el
    );
  });
};

const initOrderForm = store => {
  document.querySelectorAll('[data-shortlist-order-form]').forEach(el => {
    const action = el.getAttribute('data-form-action');
    const returnUrl = el.getAttribute('data-return-url');
    const parsedContent = parseHtml(el.innerHTML);
    ReactDOM.render(
      <ShortlistOrderFormWrapper
        action={ action }
        returnUrl={ returnUrl }
        shortlistStore={ store }
        parsedContent={ parsedContent }
      />,
      el
    );
  });
};

const initAlert = store => {
  document.querySelectorAll('[data-shortlist-order-alert]').forEach(el => {
    ReactDOM.render(
      <ShortlistAlert
        shortlistStore={ store }
      />,
      el
    );
  });
};

const initOrder = store => {
  initEvents(store);
  initOrderForm(store);
  initAlert(store);
};

export default initOrder;
